import { useEffect, useState } from 'react';
import c from '../../constant';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Button, Box, Flex, Text, useToast } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import 'moment/locale/en-gb';
import DateRangeFormField from '../../components/form/DateRangeFormField';
import ExportExcelMissionRecord from './excel';
import CheckListFormField from '../../components/form/CheckListFormFieldNested';
import { gql, useLazyQuery } from '@apollo/client';

const QUERY_EXPORT_MISSION_RECORDS = gql`
	query exportApprovedMissionRecords($input: ExportMissionRecordInput!) {
		exportApprovedMissionRecords(input: $input) {
			id
			no
			category
			reason
			status
			step
			createdBy
			createdAt
			amountOfDays
			fromDate
			toDate
			viewers {
				id
				viewerType
				viewer {
					id
					username
					fullName
					fullNameKhmer
					department
					position
					office
				}
			}
		}
	}
`;

const filterSchema = Yup.object().shape({
	type: Yup.array()
		.of(Yup.string().oneOf(['RequestForMeetingParticipation', 'Mission', 'AttendanceRequest']))
		.nullable(),
	createdDate: Yup.string().nullable(),
});

function MissionRecordExport({ isOpen, onClose }) {
	moment.locale('en-gb');
	const { t } = useTranslation();
	const toast = useToast();
	const [clearKey, setClearKey] = useState(0);
	const [exportMissionRecord, { data, loading }] = useLazyQuery(QUERY_EXPORT_MISSION_RECORDS, {
		fetchPolicy: 'network-only',
	});

	useEffect(() => {
		if (data?.exportApprovedMissionRecords?.length === 0) {
			toast({
				title: t('Mission record does not exist'),
				status: 'warning',
				isClosable: true,
				position: 'top-right',
				duration: 3000,
			});
		}
	}, [data]);

	function callbackExport(values) {
		let dateFrom = null;
		let dateTo = null;
		if (values.createdDate) {
			const tmpDate = JSON.parse(values.createdDate);
			if (tmpDate?.from && tmpDate?.to) {
				const inputFrom = { day: tmpDate?.from?.day, month: tmpDate?.from?.month - 1, year: tmpDate?.from?.year };
				const inputTo = { day: tmpDate?.to?.day, month: tmpDate?.to?.month - 1, year: tmpDate?.to?.year };
				dateFrom = moment(inputFrom).startOf('day').toDate();
				dateTo = moment(inputTo).endOf('day').toDate();
			}
		}

		if (!dateFrom || !dateTo) {
			dateFrom = moment().startOf('month').toDate();
			dateTo = moment().toDate();
		}

		exportMissionRecord({
			variables: {
				input: {
					categories: values?.type?.length > 0 ? values.type : null,
					fromDate: dateFrom && dateTo ? dateFrom : null,
					toDate: dateFrom && dateTo ? dateTo : null,
				},
			},
		});
	}

	return (
		<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose}>
			<ModalOverlay />
			<ModalContent borderRadius={c.borderRadius}>
				<ModalHeader>{t('Export Mission Record')}</ModalHeader>
				<ModalCloseButton />

				<ModalBody>
					<Formik
						initialValues={{
							type: [],
							createdDate: '',
						}}
						validationSchema={filterSchema}
						onSubmit={callbackExport}
					>
						{(props) => (
							<Box pb="4">
								<Form>
									<CheckListFormField
										key={`category-${clearKey}`}
										label="Category"
										name="type"
										list={[
											{
												label: 'Request for Meeting Participation (Phnom Penh)',
												value: 'RequestForMeetingParticipation',
											},
											{
												label: 'Mission (Domestic and International)',
												value: 'Mission',
											},
											{
												label: 'Attendance Request: Forgot to Check-in/Check-out',
												value: 'AttendanceRequest',
											},
										]}
									/>

									<center>
										<DateRangeFormField
											key={`createdDate-${clearKey}`}
											{...props}
											label="Created Date"
											name="createdDate"
											initialDate={{ fromDate: '', toDate: '' }}
										/>{' '}
									</center>

									<Flex mt="32px" justifyContent="flex-end">
										<Button
											variant="solid"
											size="md"
											h="42px"
											w="128px"
											type="submit"
											borderRadius={c.borderRadius}
											color="#3498db"
											isLoading={loading}
										>
											{t('Get Data')}
										</Button>

										<Button
											type="button"
											size="md"
											h="42px"
											ml={4}
											w="128px"
											onClick={() => {
												props.resetForm();
												setClearKey((prev) => prev + 1);
											}}
											variant="ghost"
										>
											{t('Clear')}
										</Button>
									</Flex>
								</Form>

								{data?.exportApprovedMissionRecords?.length > 0 && (
									<Box mt="24px" borderTop="1px solid #e2e8f0" pt="16px">
										<Flex alignItems="center" justifyContent="space-between">
											<Text>
												{t('Total Rows')}: {data?.exportApprovedMissionRecords?.length}
											</Text>
											<ExportExcelMissionRecord records={data?.exportApprovedMissionRecords} />
										</Flex>
									</Box>
								)}
							</Box>
						)}
					</Formik>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}

export default MissionRecordExport;
